import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import nbrApi from '../../../services/nbrApi';
import { convertDataToFloorStructure } from '../../../services/common.utils';
import MonitorDoorCardV2 from './MonitorDoorCardV2';
import CompactRoomMonitorCellWithDoorHover from './CompactRoomMonitorCellWithDoorHover';
import { getFloorMaxBadgeRowCount, getFloorMaxBelowBlockHeightPx, resolveMonitorDoorCardFooterStyle } from './monitorDoorCardModel';
import FullPageLoader from '../../components/common/FullPageLoader';
import ReservationProfile from '../ReservationProfile/ReservationProfile';
import {
    BADGE_DISPLAY_CONFIRMATION,
    BADGE_DISPLAY_GUEST,
    readStoredBadgeDisplay,
    writeStoredBadgeDisplay,
    readStoredCompactRoomCells,
    writeStoredCompactRoomCells,
    readStoredShowUnassignedReservations,
    writeStoredShowUnassignedReservations,
    getMonitorBadgeLabel,
    getMonitorStandaloneBadgePresentation,
    getMonitorStandaloneBadgeShellStyle,
    getMonitorUnassignedBadgeOuterStyle,
    MONITOR_BADGE_CHECKIN_COLOR,
    MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX,
    MONITOR_UNASSIGNED_BADGE_ROOM_TYPE_COLOR,
    COMPACT_MONITOR_DOT_PADDING_TOP_PX,
    COMPACT_MONITOR_CIRCLE_DIAMETER_PX
} from '../../../services/monitorBadgeDisplay';
import {
    enrichMonitorRoomsWithAssignedReservations,
    getReservationAssignedRoomNormalized,
    isReservationUnassignedForMonitor,
    reservationRowForDates,
    normalizeMonitorServiceStatusKey,
    getMonitorRoomOutcomes
} from '../../../services/monitorReservationUtils';
import {
    groupMonitorFloorsByBuilding,
    getMonitorFloorRowKey,
    monitorPropertyHasBuildingData,
    readStoredBuildingGrouping,
    sortMonitorFloorsDesc,
    writeStoredBuildingGrouping
} from '../../../services/monitorBuildingGrouping';
import {
    MonitorWorkspaceControls,
    useMonitorRoomsOnly,
    useMonitorZoom
} from '../../../services/monitorWorkspace';
import { getCurrentRoleContext } from '../../layouts/nav/Menu';
import moment from 'moment';
import '../../../css/multi-property-monitor.css';

function isPortfolioManagerRole(roleName) {
    return String(roleName || '').toLowerCase() === 'portfolio manager';
}

const STAYOVER_FILTER_KEY = 'stayover';
const TURNOVER_FILTER_KEY = 'turnover';
const UNASSIGNED_FILTER_KEY = 'unassigned';

/** null = all selected (pass-through); [] = show none; array = custom multi-select */
function resolveFilterSelection(selected, allKeys) {
    if (selected === null) return allKeys;
    return Array.isArray(selected) ? selected : [];
}

function isAllFiltersSelected(selected, visibleKeys) {
    if (selected === null) return true;
    if (!visibleKeys?.length) return false;
    return visibleKeys.every((key) => selected.includes(key));
}

function toggleFilterSelection(selected, allKeys, value) {
    const current = resolveFilterSelection(selected, allKeys);
    return toggleFilterValue(current, value);
}

const STAYOVER_STATUS_META = {
    key: STAYOVER_FILTER_KEY,
    label: 'Stayovers',
    icon: 'bi-house-door',
    description: 'Guests remaining in-house through today'
};

const TURNOVER_STATUS_META = {
    key: TURNOVER_FILTER_KEY,
    label: 'Turning Over Today',
    icon: 'bi-arrow-left-right',
    description: 'Same-day arrival and departure on the same room'
};

const STAYOVER_CHIP_STYLE = { backgroundColor: '#0dcaf0', color: '#053b4a', border: 'none' };
const TURNOVER_CHIP_STYLE = { backgroundColor: '#ffc107', color: '#332701', border: 'none' };

function toggleFilterValue(list, value) {
    if (!Array.isArray(list)) return [value];
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function getRoomTypeFilterKey(room) {
    const roomTypeCode =
        room?.room_type_code != null && String(room.room_type_code).trim() !== ''
            ? String(room.room_type_code).trim()
            : '';
    const roomTypeName =
        room?.room_type_name != null && String(room.room_type_name).trim() !== ''
            ? String(room.room_type_name).trim()
            : '';
    if (!roomTypeCode && !roomTypeName) return '';
    return `${roomTypeName} (${roomTypeCode})`;
}

function normalizeReservationSearchValue(value) {
    return value == null ? '' : String(value).trim().toLowerCase();
}

function reservationRowMatchesSearch(row, normalizedSearch) {
    if (!row || !normalizedSearch) return false;
    const normalizedRow = reservationRowForDates(row);
    const nestedReservation = row?.reservation && typeof row.reservation === 'object'
        ? row.reservation
        : null;
    const searchFields = [
        normalizedRow?.confirmation_number,
        normalizedRow?.guest_last,
        normalizedRow?.last_name,
        normalizedRow?.guest_name,
        nestedReservation?.confirmation_number,
        nestedReservation?.guest_last
    ];
    return searchFields.some((field) =>
        normalizeReservationSearchValue(field).includes(normalizedSearch)
    );
}

function roomMatchesReservationSearch(room, reservationsArray, searchTerm) {
    const normalizedSearch = normalizeReservationSearchValue(searchTerm);
    if (!normalizedSearch) return true;
    const roomReservationRows = [
        room,
        ...(Array.isArray(room?.confirmation_numbers) ? room.confirmation_numbers : [])
    ];
    if (roomReservationRows.some((row) => reservationRowMatchesSearch(row, normalizedSearch))) {
        return true;
    }
    const roomNumber = room?.roomNumber ?? room?.room_number;
    if (roomNumber == null || !Array.isArray(reservationsArray)) return false;
    return reservationsArray.some((reservation) => {
        const assignedRoom = getReservationAssignedRoomNormalized(reservation);
        return assignedRoom &&
            String(assignedRoom) === String(roomNumber) &&
            reservationRowMatchesSearch(reservation, normalizedSearch);
    });
}

function isOutOfServiceRoom(room) {
    return room?.service_status === 'out_of_service' || room?.service_status === 'out_of_order';
}

/** True for in-service rooms (missing service_status treated as in service). */
function isInServiceRoom(room) {
    const key = normalizeMonitorServiceStatusKey(room?.service_status);
    return !key || key === 'in_service';
}

function filterFloors(floors, reservations, {
    reservationSearchTerm,
    selectedStatusFilters,
    selectedRoomTypeFilters,
    selectedReservationStatusFilters
}) {
    let filtered = floors || [];
    if (reservationSearchTerm.trim()) {
        filtered = filtered
            .map((floor) => ({
                ...floor,
                rooms: floor.rooms.filter((room) =>
                    roomMatchesReservationSearch(room, reservations, reservationSearchTerm)
                )
            }))
            .filter((floor) => floor.rooms.length > 0);
    }
    // null = all (no filter); [] = show none; otherwise match selected keys
    if (selectedStatusFilters !== null) {
        if (selectedStatusFilters.length === 0) return [];
        const statusSet = new Set(selectedStatusFilters);
        filtered = filtered
            .map((floor) => ({
                ...floor,
                rooms: floor.rooms.filter((room) => statusSet.has(getRoomStatusChipKey(room)))
            }))
            .filter((floor) => floor.rooms.length > 0);
    }
    if (selectedRoomTypeFilters !== null) {
        if (selectedRoomTypeFilters.length === 0) return [];
        const typeSet = new Set(selectedRoomTypeFilters);
        filtered = filtered
            .map((floor) => ({
                ...floor,
                rooms: floor.rooms.filter((room) => typeSet.has(getRoomTypeFilterKey(room)))
            }))
            .filter((floor) => floor.rooms.length > 0);
    }
    if (selectedReservationStatusFilters !== null) {
        if (selectedReservationStatusFilters.length === 0) return [];
        const reservationKeys = selectedReservationStatusFilters.filter(
            (key) => key !== UNASSIGNED_FILTER_KEY
        );
        if (reservationKeys.length === 0) return [];
        filtered = filtered
            .map((floor) => ({
                ...floor,
                rooms: floor.rooms.filter((room) =>
                    roomMatchesAnyReservationStatus(room, reservationKeys)
                )
            }))
            .filter((floor) => floor.rooms.length > 0);
    }
    return filtered;
}

function countRooms(floors) {
    return (floors || []).reduce((t, f) => t + (f.rooms?.length || 0), 0);
}

function roomTypeKeysFromFloors(floors) {
    const keys = new Set();
    (floors || []).forEach((floor) => {
        (floor.rooms || []).forEach((room) => {
            const key = getRoomTypeFilterKey(room);
            if (key) keys.add(key);
        });
    });
    return keys;
}

function filterUnassignedReservations(reservations, filteredFloors, {
    reservationSearchTerm,
    selectedStatusFilters,
    selectedRoomTypeFilters
}) {
    if (selectedStatusFilters !== null && selectedStatusFilters.length === 0) return [];
    if (selectedRoomTypeFilters !== null && selectedRoomTypeFilters.length === 0) return [];
    const hasRoomAttributeDisplayFilter =
        selectedStatusFilters !== null && selectedStatusFilters.length > 0;
    const roomTypeKeys = roomTypeKeysFromFloors(filteredFloors);
    const normalizedSearch = normalizeReservationSearchValue(reservationSearchTerm);
    const typeSet =
        selectedRoomTypeFilters !== null && selectedRoomTypeFilters.length > 0
            ? new Set(selectedRoomTypeFilters)
            : null;
    return (reservations || []).filter((reservation) => {
        if (!isReservationUnassignedForMonitor(reservation)) return false;
        if (normalizedSearch && !reservationRowMatchesSearch(reservation, normalizedSearch)) {
            return false;
        }
        const roomTypeKey = getRoomTypeFilterKey(reservation);
        if (typeSet && !typeSet.has(roomTypeKey)) {
            return false;
        }
        if (hasRoomAttributeDisplayFilter && !roomTypeKeys.has(roomTypeKey)) {
            return false;
        }
        return true;
    });
}

function isUnassignedFilterSelected(selectedReservationStatusFilters) {
    return (
        selectedReservationStatusFilters === null ||
        selectedReservationStatusFilters.includes(UNASSIGNED_FILTER_KEY)
    );
}

function isReservationStatusType(room) {
    return String(room?.status_type || '').trim().toLowerCase() === 'reservation';
}

function isRoomStatusType(room) {
    return !isReservationStatusType(room);
}

function getRoomStatusChipKey(room) {
    if (isOutOfServiceRoom(room)) {
        return room.service_status === 'out_of_order' ? 'Out of Order' : 'Out of Service';
    }
    return room.status_display_text || 'Other';
}

function getRoomStatusChipStyle(room) {
    if (isOutOfServiceRoom(room)) {
        return {
            backgroundColor: '#e5534b',
            color: '#ffffff',
            border: 'none'
        };
    }
    const footer = resolveMonitorDoorCardFooterStyle(room);
    const outline = footer.borderTop
        ? footer.borderTop.replace('2px', '1px')
        : `1px solid ${footer.color}`;
    return {
        backgroundColor: footer.backgroundColor,
        color: footer.color,
        border: outline
    };
}

function statusChipCounts(floors) {
    const counts = {};
    const samples = {};
    (floors || []).forEach((floor) => {
        floor.rooms.forEach((room) => {
            // Keep housekeeping status chips (Vacant Dirty, etc.) aligned with filters:
            // only in-service rooms, plus dedicated OOS/OOO chips.
            if (!isOutOfServiceRoom(room) && !isInServiceRoom(room)) {
                return;
            }
            if (!isOutOfServiceRoom(room) && !isRoomStatusType(room)) {
                return;
            }
            const key = getRoomStatusChipKey(room);
            counts[key] = (counts[key] || 0) + 1;
            if (!samples[key]) samples[key] = room;
        });
    });
    return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([label, n]) => ({
            label,
            n,
            style: getRoomStatusChipStyle(samples[label])
        }));
}

function getRoomReservationStatusPredicates(room) {
    const outcomes = getMonitorRoomOutcomes(room);
    return {
        stayover: !!outcomes.stayover,
        turnover: !!outcomes.turnover
    };
}

function isStayoverRoom(room) {
    return !!getRoomReservationStatusPredicates(room).stayover;
}

function isTurnoverRoom(room) {
    return !!getRoomReservationStatusPredicates(room).turnover;
}

function roomMatchesReservationStatusKey(room, key) {
    if (key === STAYOVER_FILTER_KEY) return isStayoverRoom(room);
    if (key === TURNOVER_FILTER_KEY) return isTurnoverRoom(room);
    return isReservationStatusType(room) && getRoomStatusChipKey(room) === key;
}

function roomMatchesAnyReservationStatus(room, selectedKeys) {
    if (!selectedKeys?.length) return true;
    return selectedKeys.some((key) => roomMatchesReservationStatusKey(room, key));
}

function reservationStatusChipCounts(floors) {
    const counts = {};
    const samples = {};
    let stayoverCount = 0;
    let turnoverCount = 0;
    (floors || []).forEach((floor) => {
        (floor.rooms || []).forEach((room) => {
            if (isStayoverRoom(room)) stayoverCount += 1;
            if (isTurnoverRoom(room)) turnoverCount += 1;
            if (!isReservationStatusType(room)) return;
            const key = getRoomStatusChipKey(room);
            if (!key) return;
            counts[key] = (counts[key] || 0) + 1;
            if (!samples[key]) samples[key] = room;
        });
    });
    const chips = Object.entries(counts)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
        .map(([label, n]) => ({
            key: label,
            label,
            n,
            style: getRoomStatusChipStyle(samples[label]),
            description: label
        }));
    // BFF-computed stayover / turnover chips stay at the bottom of Reservation Status.
    if (stayoverCount > 0) {
        chips.push({
            key: STAYOVER_FILTER_KEY,
            label: STAYOVER_STATUS_META.label,
            n: stayoverCount,
            style: STAYOVER_CHIP_STYLE,
            icon: STAYOVER_STATUS_META.icon,
            description: STAYOVER_STATUS_META.description
        });
    }
    if (turnoverCount > 0) {
        chips.push({
            key: TURNOVER_FILTER_KEY,
            label: TURNOVER_STATUS_META.label,
            n: turnoverCount,
            style: TURNOVER_CHIP_STYLE,
            icon: TURNOVER_STATUS_META.icon,
            description: TURNOVER_STATUS_META.description
        });
    }
    return chips;
}

function roomTypeChipCounts(floors) {
    const counts = {};
    const labels = {};
    (floors || []).forEach((floor) => {
        (floor.rooms || []).forEach((room) => {
            const key = getRoomTypeFilterKey(room);
            if (!key) return;
            counts[key] = (counts[key] || 0) + 1;
            if (!labels[key]) {
                const code = String(room.room_type_code || '').trim();
                const name = String(room.room_type_name || '').trim();
                labels[key] = code && name ? `${code} — ${name}` : code || name;
            }
        });
    });
    return Object.entries(counts)
        .sort((a, b) => b[1] - a[1] || labels[a[0]].localeCompare(labels[b[0]], undefined, { sensitivity: 'base' }))
        .map(([key, n]) => ({
            key,
            label: labels[key],
            n
        }));
}

function MultiPropertyMonitor() {
    const { roleName } = useMemo(() => getCurrentRoleContext(), []);
    const isPortfolioManager = isPortfolioManagerRole(roleName);

    const badgeLogin = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('selectedLoginData')) || {};
        } catch {
            return {};
        }
    }, []);

    const [properties, setProperties] = useState([]);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [propertyRooms, setPropertyRooms] = useState({});
    const [propertyReservations, setPropertyReservations] = useState({});
    const [loading, setLoading] = useState(false);
    const [expandedPropertyId, setExpandedPropertyId] = useState(null);
    /** null = all selected; [] = show none; array = custom. Includes OOS/OOO labels. */
    const [selectedStatusFilters, setSelectedStatusFilters] = useState(null);
    /** null = all selected; [] = show none; array = custom. */
    const [selectedRoomTypeFilters, setSelectedRoomTypeFilters] = useState(null);
    /** null = all selected; [] = show none; array = custom. Includes stayover/turnover/unassigned. */
    const [selectedReservationStatusFilters, setSelectedReservationStatusFilters] = useState(null);
    const [reservationSearchTerm, setReservationSearchTerm] = useState('');
    const [toolbarExpanded, setToolbarExpanded] = useState(true);
    const [summaryExpanded, setSummaryExpanded] = useState(true);
    const [hasAppliedPropertyScope, setHasAppliedPropertyScope] = useState(false);
    const [showReservationModal, setShowReservationModal] = useState(false);
    const [selectedConfirmationNumber, setSelectedConfirmationNumber] = useState(null);
    const [selectedAssignmentContext, setSelectedAssignmentContext] = useState(null);

    const [badgeDisplayMode, setBadgeDisplayMode] = useState(() => readStoredBadgeDisplay(badgeLogin));
    const [useCompactRoomCells, setUseCompactRoomCells] = useState(() =>
        readStoredCompactRoomCells(badgeLogin)
    );
    const [showBuildingGrouping, setShowBuildingGrouping] = useState(() =>
        readStoredBuildingGrouping(badgeLogin)
    );
    const [showUnassignedReservations, setShowUnassignedReservations] = useState(() =>
        readStoredShowUnassignedReservations(badgeLogin)
    );
    const [monitorShowDate, setMonitorShowDate] = useState(() => moment().format('YYYY-MM-DD'));
    const monitorShowDateRef = useRef(monitorShowDate);
    monitorShowDateRef.current = monitorShowDate;

    const { zoomPct, zoomOut, zoomIn, canvasStyle } = useMonitorZoom();
    const { roomsOnly, toggleRoomsOnly } = useMonitorRoomsOnly();

    useEffect(() => {
        writeStoredBadgeDisplay(badgeLogin, badgeDisplayMode);
    }, [badgeDisplayMode, badgeLogin]);
    useEffect(() => {
        writeStoredCompactRoomCells(badgeLogin, useCompactRoomCells);
    }, [useCompactRoomCells, badgeLogin]);
    useEffect(() => {
        writeStoredBuildingGrouping(badgeLogin, showBuildingGrouping);
    }, [showBuildingGrouping, badgeLogin]);
    useEffect(() => {
        writeStoredShowUnassignedReservations(badgeLogin, showUnassignedReservations);
    }, [showUnassignedReservations, badgeLogin]);
    useEffect(() => {
        if (roomsOnly) setToolbarExpanded(false);
    }, [roomsOnly]);

    /** Fill viewport via flex chain (#main-wrapper → scroll); same as MonitorIndex. */
    useLayoutEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const wrapper = document.getElementById('main-wrapper');
        const contentBody = wrapper?.querySelector('.content-body');
        const container = contentBody?.querySelector('.container-fluid');

        wrapper?.classList.add('monitor-layout-fill');
        contentBody?.classList.add('monitor-layout-fill__body');
        container?.classList.add('monitor-layout-fill__container');

        if (contentBody) {
            contentBody.style.minHeight = '0';
        }

        return () => {
            wrapper?.classList.remove('monitor-layout-fill');
            contentBody?.classList.remove('monitor-layout-fill__body');
            container?.classList.remove('monitor-layout-fill__container');
            if (contentBody) {
                contentBody.style.minHeight = '';
            }
        };
    }, []);

    const fetchProperties = async () => {
        try {
            const response = await nbrApi.post('/listproperties');
            const data = response.data;
            setProperties(data?.map((prop) => ({
                ...prop,
                value: prop.pk_id,
                label: prop.property_name
            })) || []);
        } catch (error) {
            console.error('Error fetching properties:', error);
        }
    };

    const fetchPropertyRooms = async (propertyId, propertyName) => {
        try {
            const payload = {
                properties_id: propertyId,
                show_date: monitorShowDateRef.current
            };
            const res = await nbrApi.post('/getroomstatuses', payload);
            if (res?.data) {
                const roomsData = res?.data?.rooms_array || [];
                const reservationsData = res?.data?.reservations_array || [];
                const convertedData = convertDataToFloorStructure(roomsData);
                const enrichedFromReservations = enrichMonitorRoomsWithAssignedReservations(
                    convertedData,
                    reservationsData,
                    payload.show_date
                );
                setPropertyRooms((prev) => ({
                    ...prev,
                    [propertyId]: {
                        name: propertyName,
                        rooms: enrichedFromReservations,
                        lastUpdated: new Date()
                    }
                }));
                setPropertyReservations((prev) => ({
                    ...prev,
                    [propertyId]: reservationsData
                }));
            }
        } catch (error) {
            console.error(`Error fetching rooms for property ${propertyId}:`, error);
        }
    };

    const fetchAllPropertyRooms = async (isInitialCall = false) => {
        if (selectedProperties.length === 0) return;
        if (isInitialCall) setLoading(true);
        try {
            await Promise.all(selectedProperties.map((prop) =>
                fetchPropertyRooms(prop.value, prop.label)
            ));
        } catch (error) {
            console.error('Error fetching property rooms:', error);
        } finally {
            if (isInitialCall) setLoading(false);
        }
    };

    const handlePropertyToggle = (property) => {
        setSelectedProperties((prev) => {
            const isSelected = prev.some((p) => p.value === property.value);
            if (isSelected) {
                setPropertyRooms((prevRooms) => {
                    const next = { ...prevRooms };
                    delete next[property.value];
                    return next;
                });
                setPropertyReservations((prevRes) => {
                    const next = { ...prevRes };
                    delete next[property.value];
                    return next;
                });
                if (expandedPropertyId === property.value) setExpandedPropertyId(null);
                return prev.filter((p) => p.value !== property.value);
            }
            return [...prev, property];
        });
    };

    const allFloors = useMemo(
        () => Object.values(propertyRooms).flatMap((p) => p.rooms || []),
        [propertyRooms]
    );

    const uniqueStatusOptions = useMemo(() => {
        const all = [];
        allFloors.forEach((floor) => {
            floor.rooms.forEach((room) => {
                if (!isRoomStatusType(room)) return;
                if (room.status_display_text && !all.includes(room.status_display_text)) {
                    all.push(room.status_display_text);
                }
            });
        });
        return all.sort();
    }, [allFloors]);

    const allRoomStatusFilterKeys = useMemo(() => {
        const keys = new Set(uniqueStatusOptions);
        allFloors.forEach((floor) => {
            (floor.rooms || []).forEach((room) => {
                if (!isOutOfServiceRoom(room)) return;
                keys.add(room.service_status === 'out_of_order' ? 'Out of Order' : 'Out of Service');
            });
        });
        return Array.from(keys);
    }, [uniqueStatusOptions, allFloors]);

    const uniqueReservationStatusOptions = useMemo(() => {
        const all = new Set();
        allFloors.forEach((floor) => {
            floor.rooms.forEach((room) => {
                if (isReservationStatusType(room)) {
                    const key = getRoomStatusChipKey(room);
                    if (key) all.add(key);
                }
            });
        });
        all.add(STAYOVER_FILTER_KEY);
        all.add(TURNOVER_FILTER_KEY);
        all.add(UNASSIGNED_FILTER_KEY);
        return Array.from(all);
    }, [allFloors]);

    const uniqueRoomTypeOptions = useMemo(() => {
        const byKey = new Map();
        allFloors.forEach((floor) => {
            floor.rooms.forEach((room) => {
                const key = getRoomTypeFilterKey(room);
                if (!key || byKey.has(key)) return;
                const code = String(room.room_type_code || '').trim();
                const name = String(room.room_type_name || '').trim();
                byKey.set(key, {
                    key,
                    label: code && name ? `${code} — ${name}` : code || name
                });
            });
        });
        return Array.from(byKey.values()).sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        );
    }, [allFloors]);

    const allRoomTypeFilterKeys = useMemo(
        () => uniqueRoomTypeOptions.map((opt) => opt.key),
        [uniqueRoomTypeOptions]
    );

    useEffect(() => {
        setSelectedStatusFilters((prev) => {
            if (prev === null || prev.length === 0) return prev;
            const valid = new Set(allRoomStatusFilterKeys);
            const next = prev.filter((key) => valid.has(key));
            return next.length === prev.length ? prev : next;
        });
    }, [allRoomStatusFilterKeys]);

    useEffect(() => {
        setSelectedRoomTypeFilters((prev) => {
            if (prev === null || prev.length === 0) return prev;
            const valid = new Set(allRoomTypeFilterKeys);
            const next = prev.filter((key) => valid.has(key));
            return next.length === prev.length ? prev : next;
        });
    }, [allRoomTypeFilterKeys]);

    useEffect(() => {
        setSelectedReservationStatusFilters((prev) => {
            if (prev === null || prev.length === 0) return prev;
            const valid = new Set(uniqueReservationStatusOptions);
            const next = prev.filter((key) => valid.has(key));
            return next.length === prev.length ? prev : next;
        });
    }, [uniqueReservationStatusOptions]);

    const effectiveStatusFilters =
        selectedStatusFilters === null ||
        isAllFiltersSelected(selectedStatusFilters, allRoomStatusFilterKeys)
            ? null
            : selectedStatusFilters;
    const effectiveRoomTypeFilters =
        selectedRoomTypeFilters === null ||
        isAllFiltersSelected(selectedRoomTypeFilters, allRoomTypeFilterKeys)
            ? null
            : selectedRoomTypeFilters;
    const effectiveReservationStatusFilters =
        selectedReservationStatusFilters === null ||
        isAllFiltersSelected(selectedReservationStatusFilters, uniqueReservationStatusOptions)
            ? null
            : selectedReservationStatusFilters;

    const filterOpts = {
        reservationSearchTerm,
        selectedStatusFilters: effectiveStatusFilters,
        selectedRoomTypeFilters: effectiveRoomTypeFilters,
        selectedReservationStatusFilters: effectiveReservationStatusFilters
    };

    const getFilteredRooms = useMemo(() => {
        const filtered = {};
        Object.entries(propertyRooms).forEach(([propertyId, propertyData]) => {
            filtered[propertyId] = filterFloors(
                propertyData.rooms,
                propertyReservations[propertyId] || [],
                filterOpts
            );
        });
        return filtered;
    }, [
        propertyRooms,
        propertyReservations,
        reservationSearchTerm,
        selectedStatusFilters,
        selectedRoomTypeFilters,
        selectedReservationStatusFilters
    ]);

    const getUnassignedByProperty = useMemo(() => {
        const next = {};
        selectedProperties.forEach((property) => {
            const reservations = propertyReservations[property.value] || [];
            if (!isUnassignedFilterSelected(effectiveReservationStatusFilters)) {
                next[property.value] = [];
                return;
            }
            // Use floors with reservation filter omitted so unassigned isn't tied to room matches
            const floorsForUnassigned = filterFloors(
                propertyRooms[property.value]?.rooms || [],
                reservations,
                {
                    reservationSearchTerm,
                    selectedStatusFilters: effectiveStatusFilters,
                    selectedRoomTypeFilters: effectiveRoomTypeFilters,
                    selectedReservationStatusFilters: null
                }
            );
            next[property.value] = filterUnassignedReservations(
                reservations,
                floorsForUnassigned,
                {
                    reservationSearchTerm,
                    selectedStatusFilters: effectiveStatusFilters,
                    selectedRoomTypeFilters: effectiveRoomTypeFilters
                }
            );
        });
        return next;
    }, [
        selectedProperties,
        propertyReservations,
        propertyRooms,
        reservationSearchTerm,
        effectiveStatusFilters,
        effectiveRoomTypeFilters,
        effectiveReservationStatusFilters
    ]);

    const filtersActive =
        selectedStatusFilters !== null ||
        selectedRoomTypeFilters !== null ||
        selectedReservationStatusFilters !== null ||
        reservationSearchTerm.trim() !== '';

    const handleRoomStatusChipClick = (propertyId, label) => {
        setExpandedPropertyId(propertyId);
        setSelectedStatusFilters((prev) =>
            toggleFilterSelection(prev, allRoomStatusFilterKeys, label)
        );
    };

    const handleReservationStatusChipClick = (propertyId, key) => {
        setExpandedPropertyId(propertyId);
        if (key === UNASSIGNED_FILTER_KEY) {
            setShowUnassignedReservations(true);
        }
        setSelectedReservationStatusFilters((prev) =>
            toggleFilterSelection(prev, uniqueReservationStatusOptions, key)
        );
    };

    const handleRoomTypeChipClick = (propertyId, key) => {
        setExpandedPropertyId(propertyId);
        setSelectedRoomTypeFilters((prev) =>
            toggleFilterSelection(prev, allRoomTypeFilterKeys, key)
        );
    };

    const handleToggleShowAllRoomStatus = (propertyId, visibleKeys) => {
        setExpandedPropertyId(propertyId);
        setSelectedStatusFilters((prev) =>
            isAllFiltersSelected(prev, visibleKeys) ? [] : null
        );
    };

    const handleToggleShowAllReservationStatus = (propertyId, visibleKeys) => {
        setExpandedPropertyId(propertyId);
        setSelectedReservationStatusFilters((prev) =>
            isAllFiltersSelected(prev, visibleKeys) ? [] : null
        );
    };

    const handleToggleShowAllRoomTypes = (propertyId, visibleKeys) => {
        setExpandedPropertyId(propertyId);
        setSelectedRoomTypeFilters((prev) =>
            isAllFiltersSelected(prev, visibleKeys) ? [] : null
        );
    };

    const isRoomStatusChipSelected = (label) =>
        selectedStatusFilters === null || selectedStatusFilters.includes(label);

    const isReservationStatusChipSelected = (key) =>
        selectedReservationStatusFilters === null ||
        selectedReservationStatusFilters.includes(key);

    const isRoomTypeChipSelected = (key) =>
        selectedRoomTypeFilters === null || selectedRoomTypeFilters.includes(key);

    useEffect(() => {
        let intervalId;
        const startRefreshing = () => {
            fetchAllPropertyRooms(true);
            if (!intervalId) {
                intervalId = setInterval(() => {
                    if (document.visibilityState === 'visible') fetchAllPropertyRooms();
                }, 5000);
            }
        };
        const stopRefreshing = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };
        if (selectedProperties.length > 0 && document.visibilityState === 'visible') {
            startRefreshing();
        }
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && selectedProperties.length > 0) {
                startRefreshing();
            } else {
                stopRefreshing();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            stopRefreshing();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [selectedProperties, monitorShowDate]);

    useEffect(() => {
        fetchProperties();
    }, []);

    useEffect(() => {
        if (!properties.length || hasAppliedPropertyScope) return;

        if (isPortfolioManager) {
            // Interim: all active properties until portfolio-manager scoping is provided
            const activeProperties = properties.filter(
                (property) => String(property.status_val || '').toLowerCase() === 'active'
            );
            setSelectedProperties(
                activeProperties.map((property) => ({
                    value: property.value,
                    label: property.label
                }))
            );
        } else {
            const loginPropertyId = badgeLogin?.properties_id;
            const loginProperty = properties.find(
                (property) => String(property.value) === String(loginPropertyId)
            );
            setSelectedProperties(
                loginProperty
                    ? [{ value: loginProperty.value, label: loginProperty.label }]
                    : []
            );
        }
        setHasAppliedPropertyScope(true);
    }, [properties, isPortfolioManager, badgeLogin, hasAppliedPropertyScope]);

    useEffect(() => {
        if (selectedProperties.length === 0) {
            setExpandedPropertyId(null);
            return;
        }
        setExpandedPropertyId((id) => {
            if (id != null && selectedProperties.some((property) => property.value === id)) {
                return id;
            }
            return selectedProperties[0].value;
        });
    }, [selectedProperties]);

    const handleConfirmationBadgeClick = (confirmationNumber, assignmentContext) => {
        setSelectedConfirmationNumber(confirmationNumber);
        setSelectedAssignmentContext(
            assignmentContext === 'assign' || assignmentContext === 'show' ? assignmentContext : null
        );
        setShowReservationModal(true);
    };

    const handleCompactBadgeClick = (conf) => {
        const { confirmationForClick } = getMonitorBadgeLabel(conf, badgeDisplayMode);
        handleConfirmationBadgeClick(confirmationForClick, 'show');
    };

    const renderConfirmationBadge = (conf, isUnassigned = false) => {
        const pres = getMonitorStandaloneBadgePresentation(conf, badgeDisplayMode, { isUnassigned });
        const confirmationNumber = typeof conf === 'object' && conf != null ? conf.confirmation_number : conf;
        const key = pres.confirmationForClick || confirmationNumber;
        const hoverHandlers = {
            onMouseEnter: (e) => {
                e.currentTarget.style.opacity = '0.8';
            },
            onMouseLeave: (e) => {
                e.currentTarget.style.opacity = '1';
            }
        };

        if (pres.kind === 'unassigned') {
            return (
                <div
                    key={key}
                    onClick={() => handleConfirmationBadgeClick(pres.confirmationForClick, 'assign')}
                    className="text-center text-decoration-none"
                    style={getMonitorUnassignedBadgeOuterStyle()}
                    {...hoverHandlers}
                    title={pres.tooltip}
                >
                    <div
                        style={{
                            backgroundColor: MONITOR_BADGE_CHECKIN_COLOR,
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX,
                            lineHeight: 1.2,
                            padding: '2px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        <span
                            aria-hidden
                            style={{ fontSize: Math.max(7, MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX - 4), lineHeight: 1 }}
                        >
                            ▲
                        </span>
                        <span
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {pres.display}
                        </span>
                    </div>
                    <div
                        style={{
                            backgroundColor: '#fff',
                            color: MONITOR_UNASSIGNED_BADGE_ROOM_TYPE_COLOR,
                            fontWeight: 'bold',
                            fontSize: MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX + 3,
                            lineHeight: 1.2,
                            padding: '5px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {pres.roomTypeCode}
                    </div>
                </div>
            );
        }

        return (
            <div
                key={key}
                onClick={() =>
                    handleConfirmationBadgeClick(
                        pres.confirmationForClick,
                        isUnassigned ? 'assign' : 'show'
                    )
                }
                className="text-center text-decoration-none"
                style={getMonitorStandaloneBadgeShellStyle(pres)}
                {...hoverHandlers}
                title={pres.tooltip}
            >
                {pres.display}
            </div>
        );
    };

    const renderUnassignedReservationsFloorRow = (reservationsList) => {
        if (!showUnassignedReservations || !reservationsList || reservationsList.length === 0) {
            return null;
        }

        return (
            <div className="card shadow-sm border-0 bg-white monitor-grouped-room-card monitor-unassigned-floor mb-3">
                <div className="row">
                    <div className="col-12 mx-0">
                        <div className="card-body py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 text-primary fw-bold">
                                    <i className="bi bi-calendar-x me-2"></i>
                                    Unassigned Reservations
                                </h5>
                                <span className="badge bg-secondary fs-6 px-3 py-2">
                                    {reservationsList.length} Reservation{reservationsList.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <hr className="monitor-unassigned-floor__divider" />
                <div className="monitor-unassigned-floor__badges monitor-standalone-badge-stack">
                    {reservationsList.map((reservation, index) => {
                        const conf = typeof reservation === 'object'
                            ? reservation
                            : { confirmation_number: reservation };
                        const key = conf.confirmation_number || conf.reservations_id || index;
                        return (
                            <div key={key}>
                                {renderConfirmationBadge(conf, true)}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const applyMonitorShowDate = (nextDate) => {
        if (!nextDate) return;
        setMonitorShowDate(nextDate);
        monitorShowDateRef.current = nextDate;
        fetchAllPropertyRooms(true);
    };

    const shiftMonitorShowDate = (dayDelta) => {
        applyMonitorShowDate(
            moment(monitorShowDate, 'YYYY-MM-DD').add(dayDelta, 'days').format('YYYY-MM-DD')
        );
    };

    const renderRoomCell = (room, badgeSlotRowCount, footerSlotHeightPx) =>
        useCompactRoomCells ? (
            <CompactRoomMonitorCellWithDoorHover
                key={room.roomNumber}
                room={room}
                badgeDisplayMode={badgeDisplayMode}
                onBadgeClick={handleCompactBadgeClick}
                onConfirmationClick={handleConfirmationBadgeClick}
                footerSlotHeightPx={footerSlotHeightPx}
            />
        ) : (
            <MonitorDoorCardV2
                key={room.roomNumber}
                data={room}
                roomNumber={room.roomNumber}
                status={room.status}
                statusColor={room.statusColor}
                isDnd={room.isDnd}
                show_icon={room.show_icon}
                icon_blink_interval={room.icon_blink_interval}
                isMonitor={true}
                onConfirmationClick={handleConfirmationBadgeClick}
                badgeDisplayMode={badgeDisplayMode}
                badgeSlotRowCount={badgeSlotRowCount}
                footerSlotHeightPx={footerSlotHeightPx}
            />
        );

    const floorLabelStyle = useMemo(
        () =>
            useCompactRoomCells
                ? {
                      width: 52,
                      minWidth: 52,
                      fontSize: 38,
                      fontWeight: 500,
                      lineHeight: 1,
                      // Sit in the same vertical band as the circle (below reservation dots).
                      marginTop: COMPACT_MONITOR_DOT_PADDING_TOP_PX,
                      height: COMPACT_MONITOR_CIRCLE_DIAMETER_PX
                  }
                : { width: 130, minWidth: 130, fontSize: 90, fontWeight: 500 },
        [useCompactRoomCells]
    );

    const monitorFloorRoomsRowClass = useCompactRoomCells
        ? ' align-items-end monitor-compact-floor-rooms'
        : ' align-items-start monitor-door-card-floor-rooms';

    const renderFloorRow = (propertyId, floor) => {
        const badgeSlotRowCount = getFloorMaxBadgeRowCount(floor.rooms, badgeDisplayMode);
        const footerSlotHeightPx = getFloorMaxBelowBlockHeightPx(floor.rooms, {
            compact: useCompactRoomCells
        });
        return (
            <div
                key={`${propertyId}-${getMonitorFloorRowKey(floor)}`}
                className="d-flex align-items-start gap-3 mb-3"
            >
                <h4
                    className="mb-0 text-start d-flex align-items-center justify-content-center flex-shrink-0 text-muted"
                    style={floorLabelStyle}
                >
                    {floor.floor}
                </h4>
                <div
                    className={`d-flex flex-wrap gap-1${monitorFloorRoomsRowClass}`}
                >
                    {floor.rooms.map((room) =>
                        renderRoomCell(room, badgeSlotRowCount, footerSlotHeightPx)
                    )}
                </div>
            </div>
        );
    };

    const renderFloorsSection = (propertyId, floors) => {
        const sortedFloors = sortMonitorFloorsDesc(floors);
        const groupBuildings = showBuildingGrouping && monitorPropertyHasBuildingData(sortedFloors);
        if (!groupBuildings) {
            return sortedFloors.map((floor) => renderFloorRow(propertyId, floor));
        }
        return groupMonitorFloorsByBuilding(sortedFloors).map((building) => {
            const buildingKey = building.buildings_id ?? building.building_name;
            const roomCount = building.floors.reduce((t, f) => t + f.rooms.length, 0);
            return (
                <div key={`${propertyId}-building-${buildingKey}`} className="monitor-building-group mb-4">
                    <div className="monitor-building-group__header mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 text-primary fw-bold">
                                <i className="bi bi-building me-2"></i>
                                {building.building_name}
                            </h5>
                            <span className="badge bg-secondary fs-6 px-3 py-2">
                                {roomCount} Room{roomCount !== 1 ? 's' : ''}
                            </span>
                        </div>
                    </div>
                    {building.floors.map((floor) => renderFloorRow(propertyId, floor))}
                </div>
            );
        });
    };

    const getUnassignedForPropertyView = (propertyId) => getUnassignedByProperty[propertyId] || [];

    const getSummaryFloors = (propertyId, omit) => {
        return filterFloors(
            propertyRooms[propertyId]?.rooms || [],
            propertyReservations[propertyId] || [],
            {
                reservationSearchTerm,
                // null omits that dimension so chip counts stay available while filtering
                selectedStatusFilters: omit === 'status' ? null : effectiveStatusFilters,
                selectedRoomTypeFilters: omit === 'roomType' ? null : effectiveRoomTypeFilters,
                selectedReservationStatusFilters:
                    omit === 'reservation' ? null : effectiveReservationStatusFilters
            }
        );
    };

    const renderPropertyView = (propertyId, { showPropertyHeader = true } = {}) => {
        const propertyData = propertyRooms[propertyId];
        if (!propertyData) {
            return <p className="text-muted mb-0">Loading…</p>;
        }

        const baseFloors = getFilteredRooms[propertyId] || [];
        const unassignedList = getUnassignedForPropertyView(propertyId);
        const unassignedRow = renderUnassignedReservationsFloorRow(unassignedList);

        const propertyHeader = showPropertyHeader ? (
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 text-primary">{propertyData.name}</h5>
                <span className="badge bg-primary fs-6 px-3 py-2">
                    {countRooms(baseFloors)} Rooms
                </span>
            </div>
        ) : null;

        const floors = sortMonitorFloorsDesc(baseFloors);
        if (!floors.length) {
            return (
                <>
                    {propertyHeader}
                    {unassignedRow}
                    {!unassignedRow && (
                        <p className="text-muted mb-0">No rooms match the current filters.</p>
                    )}
                </>
            );
        }
        return (
            <div>
                {propertyHeader}
                {unassignedRow}
                {renderFloorsSection(propertyId, floors)}
            </div>
        );
    };

    const totalFilteredRooms = Object.values(getFilteredRooms).reduce(
        (t, floors) => t + countRooms(floors),
        0
    );

    const renderFilterSummary = () => {
        if (selectedProperties.length === 0) return null;
        const activeProperty =
            selectedProperties.find((property) => property.value === expandedPropertyId) ||
            selectedProperties[0];
        if (!activeProperty) return null;
        const propertyId = activeProperty.value;
        const statusChips = statusChipCounts(getSummaryFloors(propertyId, 'status'));
        const reservationChips = reservationStatusChipCounts(
            getSummaryFloors(propertyId, 'reservation')
        );
        const roomTypeChips = roomTypeChipCounts(getSummaryFloors(propertyId, 'roomType'));
        const unassignedCount = showUnassignedReservations
            ? filterUnassignedReservations(
                propertyReservations[propertyId] || [],
                getSummaryFloors(propertyId, 'reservation'),
                {
                    reservationSearchTerm,
                    selectedStatusFilters: effectiveStatusFilters,
                    selectedRoomTypeFilters: effectiveRoomTypeFilters
                }
            ).length
            : 0;
        const reservationVisibleKeys = [
            ...reservationChips.map((chip) => chip.key),
            ...(unassignedCount > 0 ? [UNASSIGNED_FILTER_KEY] : [])
        ];
        const statusVisibleKeys = statusChips.map((chip) => chip.label);
        const roomTypeVisibleKeys = roomTypeChips.map((chip) => chip.key);
        const statusShowNone = isAllFiltersSelected(selectedStatusFilters, statusVisibleKeys);
        const reservationShowNone = isAllFiltersSelected(
            selectedReservationStatusFilters,
            reservationVisibleKeys
        );
        const roomTypeShowNone = isAllFiltersSelected(selectedRoomTypeFilters, roomTypeVisibleKeys);
        const filteredRoomCount = countRooms(getFilteredRooms[propertyId] || []);
        return (
            <div className="mpm-summary-section">
                <div className="mpm-summary-section__header">
                    {!summaryExpanded && (
                        <div className="mpm-summary-section__collapsed-label text-muted small">
                            Filter summary · {selectedProperties.length}{' '}
                            {selectedProperties.length === 1 ? 'property' : 'properties'}
                        </div>
                    )}
                    <button
                        type="button"
                        className={`btn btn-sm flex-shrink-0 ${summaryExpanded ? 'btn-outline-secondary' : 'btn-primary'}`}
                        onClick={() => setSummaryExpanded((v) => !v)}
                        aria-expanded={summaryExpanded}
                    >
                        <i
                            className={`bi ${summaryExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}
                            aria-hidden
                        />
                        {summaryExpanded ? 'Hide filter summary' : 'Show filter summary'}
                    </button>
                </div>
                {summaryExpanded && (
                    <div className="mpm-summary-tabbed">
                        <ul className="nav nav-tabs mpm-summary-property-tabs" role="tablist">
                            {selectedProperties.map((property) => {
                                const isActive = property.value === propertyId;
                                return (
                                    <li className="nav-item" key={property.value} role="presentation">
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            className={`nav-link${isActive ? ' active' : ''}`}
                                            onClick={() => setExpandedPropertyId(property.value)}
                                        >
                                            <span className="mpm-summary-tab-label">{property.label}</span>
                                            <span className="mpm-summary-tab-count">
                                                {countRooms(getFilteredRooms[property.value] || [])}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="mpm-summary-property is-active">
                            <div className="mpm-summary-property__header">
                                <div className="fw-bold text-truncate mpm-summary-card__title">
                                    {activeProperty.label}
                                </div>
                                <div className="small text-muted">{filteredRoomCount} rooms</div>
                            </div>
                            <div className="mpm-summary-panels">
                                <div className="mpm-summary-card mpm-summary-panel text-start">
                                    <div className="mpm-summary-panel__heading">
                                        <div className="mpm-summary-panel__title">
                                            <i className="bi bi-tag me-1" aria-hidden />
                                            Room Status
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm py-0 px-1 mpm-summary-show-all"
                                            onClick={() =>
                                                handleToggleShowAllRoomStatus(propertyId, statusVisibleKeys)
                                            }
                                        >
                                            {statusShowNone ? 'Show none' : 'Show all'}
                                        </button>
                                    </div>
                                    <div className="mpm-summary-card__chips">
                                        {statusChips.map(({ label, n, style }) => (
                                            <button
                                                type="button"
                                                key={label}
                                                className={`mpm-summary-chip-row${isRoomStatusChipSelected(label) ? ' is-selected' : ''}`}
                                                onClick={() => handleRoomStatusChipClick(propertyId, label)}
                                                title={`Toggle ${label} (multi-select)`}
                                            >
                                                <span className="mpm-summary-chip-count">{n}</span>
                                                <span className="badge mpm-status-chip" style={style}>
                                                    {label}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mpm-summary-card mpm-summary-panel text-start">
                                    <div className="mpm-summary-panel__heading">
                                        <div className="mpm-summary-panel__title">
                                            <i className="bi bi-calendar-event me-1" aria-hidden />
                                            Reservation Status
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm py-0 px-1 mpm-summary-show-all"
                                            onClick={() =>
                                                handleToggleShowAllReservationStatus(
                                                    propertyId,
                                                    reservationVisibleKeys
                                                )
                                            }
                                        >
                                            {reservationShowNone ? 'Show none' : 'Show all'}
                                        </button>
                                    </div>
                                    <div className="mpm-summary-card__chips">
                                        {reservationChips.length === 0 && unassignedCount === 0 ? (
                                            <div className="text-muted small px-1">No reservation statuses</div>
                                        ) : (
                                            <>
                                                {reservationChips.map(({ key, label, n, style, icon, description }) => (
                                                    <button
                                                        type="button"
                                                        key={key}
                                                        className={`mpm-summary-chip-row${isReservationStatusChipSelected(key) ? ' is-selected' : ''}`}
                                                        onClick={() => handleReservationStatusChipClick(propertyId, key)}
                                                        title={description || label}
                                                    >
                                                        <span className="mpm-summary-chip-count">{n}</span>
                                                        <span className="badge mpm-status-chip" style={style}>
                                                            {icon ? <i className={`bi ${icon} me-1`} aria-hidden /> : null}
                                                            {label}
                                                        </span>
                                                    </button>
                                                ))}
                                                {unassignedCount > 0 && (
                                                    <button
                                                        type="button"
                                                        className={`mpm-summary-chip-row${isReservationStatusChipSelected(UNASSIGNED_FILTER_KEY) ? ' is-selected' : ''}`}
                                                        title={`${unassignedCount} unassigned — click to filter`}
                                                        onClick={() =>
                                                            handleReservationStatusChipClick(
                                                                propertyId,
                                                                UNASSIGNED_FILTER_KEY
                                                            )
                                                        }
                                                    >
                                                        <span className="mpm-summary-chip-count">{unassignedCount}</span>
                                                        <span className="badge mpm-status-chip bg-danger">unassigned</span>
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="mpm-summary-card mpm-summary-panel text-start">
                                    <div className="mpm-summary-panel__heading">
                                        <div className="mpm-summary-panel__title">
                                            <i className="bi bi-door-open me-1" aria-hidden />
                                            Room Type
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm py-0 px-1 mpm-summary-show-all"
                                            onClick={() =>
                                                handleToggleShowAllRoomTypes(propertyId, roomTypeVisibleKeys)
                                            }
                                        >
                                            {roomTypeShowNone ? 'Show none' : 'Show all'}
                                        </button>
                                    </div>
                                    <div className="mpm-summary-card__chips">
                                        {roomTypeChips.length === 0 ? (
                                            <div className="text-muted small px-1">No room types</div>
                                        ) : (
                                            roomTypeChips.map(({ key, label, n }) => (
                                                <button
                                                    type="button"
                                                    key={key}
                                                    className={`mpm-summary-chip-row${isRoomTypeChipSelected(key) ? ' is-selected' : ''}`}
                                                    onClick={() => handleRoomTypeChipClick(propertyId, key)}
                                                    title={`Toggle ${label} (multi-select)`}
                                                >
                                                    <span className="mpm-summary-chip-count">{n}</span>
                                                    <span className="badge mpm-status-chip bg-secondary">{label}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading && selectedProperties.length > 0 && Object.keys(propertyRooms).length === 0) {
        return <FullPageLoader />;
    }

    return (
        <div className={`multi-property-monitor monitor-index-page monitor-index-page--floor${useCompactRoomCells ? ' monitor-index-page--compact-cells' : ''}`}>
            {roomsOnly && (
                <MonitorWorkspaceControls
                    floating
                    title="Room Status Monitor"
                    roomsOnly={roomsOnly}
                    onToggleRoomsOnly={toggleRoomsOnly}
                    zoomPct={zoomPct}
                    onZoomOut={zoomOut}
                    onZoomIn={zoomIn}
                />
            )}

            <div className={`monitor-index-toolbar${roomsOnly ? ' monitor-kiosk-hide' : ''}`}>
                <div
                    className={`monitor-index-toolbar__header${toolbarExpanded ? ' monitor-index-toolbar__header--expanded' : ''}`}
                >
                    {!toolbarExpanded && (
                        <div className="monitor-index-toolbar__summary text-muted small">
                            {selectedProperties.length}{' '}
                            {selectedProperties.length === 1 ? 'property' : 'properties'} ·{' '}
                            {totalFilteredRooms} rooms · {moment(monitorShowDate).format('MMM D, YYYY')}
                        </div>
                    )}
                    <div className="monitor-index-toolbar__actions">
                        <MonitorWorkspaceControls
                            roomsOnly={roomsOnly}
                            onToggleRoomsOnly={toggleRoomsOnly}
                            zoomPct={zoomPct}
                            onZoomOut={zoomOut}
                            onZoomIn={zoomIn}
                        />
                        <button
                            type="button"
                            className={`btn btn-sm flex-shrink-0 ${toolbarExpanded ? 'btn-outline-secondary' : 'btn-primary'}`}
                            onClick={() => setToolbarExpanded((v) => !v)}
                        >
                            <i
                                className={`bi ${toolbarExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}
                                aria-hidden
                            />
                            {toolbarExpanded ? 'Hide toolbar' : 'Show toolbar'}
                        </button>
                    </div>
                </div>

                {toolbarExpanded && (
                    <div className="card shadow-sm border-0 monitor-toolbar-preferences mb-0">
                        <div className="card-body px-3 px-lg-4 py-3">
                            {isPortfolioManager && (
                                <div className="mpm-toolbar-section">
                                    <label className="monitor-toolbar-label form-label fw-bold text-primary mb-2">
                                        Properties
                                    </label>
                                    <div className="mpm-property-chips">
                                        {properties
                                            .filter((property) => String(property.status_val || '').toLowerCase() === 'active')
                                            .map((property) => {
                                                const isSelected = selectedProperties.some((p) => p.value === property.value);
                                                return (
                                                    <button
                                                        key={property.value}
                                                        type="button"
                                                        className={`mpm-property-chip${isSelected ? ' is-selected' : ''}`}
                                                        onClick={() => handlePropertyToggle(property)}
                                                    >
                                                        {property.label}
                                                    </button>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}

                            <div className={`monitor-toolbar-cluster${isPortfolioManager ? ' mt-3' : ''}`}>
                                <div className="monitor-toolbar-item monitor-toolbar-date-wrap">
                                    <label htmlFor="mpmShowDate" className="monitor-toolbar-label form-label fw-bold text-primary">
                                        Show date
                                    </label>
                                    <div className="monitor-toolbar-date-control monitor-toolbar-control">
                                        <button type="button" className="btn btn-outline-secondary monitor-toolbar-date-step" onClick={() => shiftMonitorShowDate(-1)} aria-label="Previous day">
                                            <i className="bi bi-chevron-left" aria-hidden />
                                        </button>
                                        <input
                                            id="mpmShowDate"
                                            type="date"
                                            className="form-control monitor-toolbar-date-input"
                                            value={monitorShowDate}
                                            onChange={(e) => applyMonitorShowDate(e.target.value)}
                                        />
                                        <button type="button" className="btn btn-outline-secondary monitor-toolbar-date-step" onClick={() => shiftMonitorShowDate(1)} aria-label="Next day">
                                            <i className="bi bi-chevron-right" aria-hidden />
                                        </button>
                                    </div>
                                </div>
                                <div className="monitor-toolbar-item">
                                    <label className="monitor-toolbar-label form-label fw-bold text-primary">Show</label>
                                    <div className="btn-group monitor-toolbar-control" role="group">
                                        <input type="radio" className="btn-check" name="mpmBadge" id="mpmBadgeConf" checked={badgeDisplayMode === BADGE_DISPLAY_CONFIRMATION} onChange={() => setBadgeDisplayMode(BADGE_DISPLAY_CONFIRMATION)} />
                                        <label className="btn btn-outline-secondary" htmlFor="mpmBadgeConf">Conf #</label>
                                        <input type="radio" className="btn-check" name="mpmBadge" id="mpmBadgeGuest" checked={badgeDisplayMode === BADGE_DISPLAY_GUEST} onChange={() => setBadgeDisplayMode(BADGE_DISPLAY_GUEST)} />
                                        <label className="btn btn-outline-secondary" htmlFor="mpmBadgeGuest">Guest</label>
                                    </div>
                                </div>
                                <div className="monitor-toolbar-item">
                                    <label className="monitor-toolbar-label form-label fw-bold text-primary">Room layout</label>
                                    <div className="btn-group monitor-toolbar-control" role="group">
                                        <input type="radio" className="btn-check" name="mpmLayout" id="mpmDoor" checked={!useCompactRoomCells} onChange={() => setUseCompactRoomCells(false)} />
                                        <label className="btn btn-outline-secondary" htmlFor="mpmDoor">Door cards</label>
                                        <input type="radio" className="btn-check" name="mpmLayout" id="mpmCompact" checked={useCompactRoomCells} onChange={() => setUseCompactRoomCells(true)} />
                                        <label className="btn btn-outline-secondary" htmlFor="mpmCompact">Compact</label>
                                    </div>
                                </div>
                                <div className="monitor-toolbar-item monitor-toolbar-filter-wrap">
                                    <label htmlFor="mpmResSearch" className="monitor-toolbar-label form-label fw-bold text-primary">
                                        Filter by Reservation
                                    </label>
                                    <input
                                        id="mpmResSearch"
                                        type="search"
                                        className="form-control monitor-toolbar-reservation-search monitor-toolbar-control"
                                        value={reservationSearchTerm}
                                        onChange={(e) => setReservationSearchTerm(e.target.value)}
                                        placeholder="Confirmation # or last name"
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="monitor-toolbar-item">
                                    <label className="monitor-toolbar-label form-label fw-bold text-primary">Unassigned reservations</label>
                                    <div className="btn-group monitor-toolbar-control" role="group">
                                        <input type="radio" className="btn-check" name="mpmUnassigned" id="mpmUnassignedShow" checked={showUnassignedReservations} onChange={() => setShowUnassignedReservations(true)} />
                                        <label className="btn btn-outline-secondary" htmlFor="mpmUnassignedShow">Show</label>
                                        <input type="radio" className="btn-check" name="mpmUnassigned" id="mpmUnassignedHide" checked={!showUnassignedReservations} onChange={() => setShowUnassignedReservations(false)} />
                                        <label className="btn btn-outline-secondary" htmlFor="mpmUnassignedHide">Hide</label>
                                    </div>
                                </div>
                                <div className="monitor-toolbar-item">
                                    <label className="monitor-toolbar-label form-label fw-bold text-primary">Building grouping</label>
                                    <div className="btn-group monitor-toolbar-control" role="group">
                                        <input type="radio" className="btn-check" name="mpmBuildings" id="mpmBuildingsOn" checked={showBuildingGrouping} onChange={() => setShowBuildingGrouping(true)} />
                                        <label className="btn btn-outline-secondary" htmlFor="mpmBuildingsOn">On</label>
                                        <input type="radio" className="btn-check" name="mpmBuildings" id="mpmBuildingsOff" checked={!showBuildingGrouping} onChange={() => setShowBuildingGrouping(false)} />
                                        <label className="btn btn-outline-secondary" htmlFor="mpmBuildingsOff">Off</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {renderFilterSummary()}

            <div className="monitor-zoom-canvas" style={canvasStyle}>
            <div className="monitor-index-scroll">
                    <div className="monitor-index-scroll-content">
                {selectedProperties.length === 0 ? (
                    <div className="card shadow-sm border-0">
                        <div className="card-body empty-state">
                            <i className="bi bi-building"></i>
                            <h4>No Properties Selected</h4>
                            <p>
                                {isPortfolioManager
                                    ? 'Select properties above to monitor a portfolio of hotels.'
                                    : 'No property is available for the current login.'}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {expandedPropertyId && (
                            <div className="card shadow-sm border-0">
                                <div className="card-body">
                                    {renderPropertyView(expandedPropertyId)}
                                </div>
                            </div>
                        )}

                        {!filtersActive && !expandedPropertyId && (
                            <p className="text-muted">Select a property card to open its room grid.</p>
                        )}
                    </>
                )}
                    </div>
                </div>
            </div>

            {showReservationModal && (
                <ReservationProfile
                    show={showReservationModal}
                    onClose={() => {
                        setShowReservationModal(false);
                        setSelectedConfirmationNumber(null);
                        setSelectedAssignmentContext(null);
                    }}
                    confirmationNumber={selectedConfirmationNumber}
                    assignmentContext={selectedAssignmentContext}
                />
            )}
        </div>
    );
}

export default MultiPropertyMonitor;
